import { getIvsPlayerConfig } from "../../../../Component/ivs/playerConfig";
import { create, isPlayerSupported, LogLevel, PlayerEventType, PlayerState } from "amazon-ivs-player";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { restoreSession } from "../../../../Component/axios_Interceptor/api";
import api2 from "../../../../Component/axios_Interceptor/api2";
import url from "../../../../Component/server";

interface LiveCardPreviewProps {
  liveSeq: number;
  isLogin: boolean;
}

export default function LiveCardPreview({ liveSeq, isLogin }: LiveCardPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [visible, setVisible] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [cameraOff, setCameraOff] = useState<boolean | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {
      rootMargin: "160px",
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || !isPlayerSupported || !videoRef.current) {
      setPlaying(false);
      return;
    }

    setPlaying(false);
    setCameraOff(null);
    const controller = new AbortController();
    const player = create(getIvsPlayerConfig());
    let disposed = false;
    let hasPlayback = false;
    let hasStarted = false;
    let accessDenied = false;
    let loading = false;
    player.setLogLevel(LogLevel.ERROR);
    player.setMuted(true);
    player.setVolume(0);
    player.setLiveLowLatencyEnabled(true);
    player.attachHTMLVideoElement(videoRef.current);

    const onReady = () => {
      void Promise.resolve(player.play()).catch(() => {
        if (!disposed && !hasStarted) setPlaying(false);
      });
    };
    const onPlaying = () => {
      if (!disposed) {
        hasPlayback = true;
        hasStarted = true;
        setPlaying(true);
      }
    };
    const onUnavailable = () => {
      if (!disposed) {
        hasPlayback = false;
        // A short stream interruption should not flash the waiting thumbnail over video.
        if (!hasStarted) setPlaying(false);
      }
    };
    const onMetadata = (cue: { text: string }) => {
      if (disposed) return;
      try {
        const metadata = JSON.parse(cue.text);
        const muted = metadata.action === "camera_muted" ? metadata.value : metadata.camera_muted;
        if (typeof muted === "boolean") setCameraOff(muted);
      } catch {
        // Other timed metadata must not reveal video before the camera state arrives.
      }
    };
    player.addEventListener(PlayerState.READY, onReady);
    player.addEventListener(PlayerState.PLAYING, onPlaying);
    player.addEventListener(PlayerState.ENDED, onUnavailable);
    player.addEventListener(PlayerEventType.ERROR, onUnavailable);
    player.addEventListener(PlayerEventType.TEXT_METADATA_CUE, onMetadata);

    const load = async () => {
      if (disposed || loading || accessDenied) return;
      loading = true;
      try {
        const token = isLogin ? await restoreSession() : null;
        if (disposed) return;
        const { data } = await api2.get(url.Communication_url.playback_info, {
          params: { live_seq: liveSeq },
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          signal: controller.signal,
        });
        if (!disposed && data?.playUrl) player.load(data.playUrl);
      } catch (error: any) {
        if (error?.response?.status === 403) accessDenied = true;
        // Keep the loading cover until the current stream is available.
      } finally {
        loading = false;
      }
    };
    void load();
    const retry = window.setInterval(() => { if (!hasPlayback) void load(); }, 5000);

    return () => {
      disposed = true;
      window.clearInterval(retry);
      controller.abort();
      player.delete();
    };
  }, [visible, liveSeq, isLogin]);

  return <Preview ref={containerRef}>
    <video aria-hidden="true" crossOrigin="anonymous" ref={videoRef} autoPlay muted playsInline className={playing && cameraOff === false ? "playing" : ""} />
    {cameraOff === true
      ? <Loading role="status">화면 가림 처리 중</Loading>
      : (!playing || cameraOff === null) && <Loading>방송 화면 확인 중</Loading>}
  </Preview>;
}

const Preview = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  // The saved thumbnail can show the seller even when their camera is off.
  background: #050505;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
  }

  video.playing { opacity: 1; }
`;

const Loading = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c9c4d1;
  font-size: 12px;
`;
